import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object, string } from 'yup';

import { Check, CheckItem, CheckItemDef } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { useCheckPrints } from 'Src/feature/check-print/useCheckPrints';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useForm } from 'Src/feature/form/useForm';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useSeatAssignment } from 'Src/feature/seat-assignment/useSeatAssignment';
import { useToast } from 'Src/feature/toasts/useToast';
import { useVoidReason } from 'Src/feature/void-reason/useVoidReason';
import useRoute from 'Src/routes/useRoute';

const sectionId = 'useCurrentCheckItemButtons';

const sortByPosition = (a: UsedCurrentCheckItemButtons['activeCheckItems'][number], b: UsedCurrentCheckItemButtons['activeCheckItems'][number]) => a.position - b.position;

interface UseCheckItemButtonsOptions {
  mode: 'readonly' | 'edit-one' | 'edit-many';
}

const useCurrentCheckItemButtons = (options: UseCheckItemButtonsOptions) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const { mode } = options;
  const { crumbs, navigate, route } = useRoute();
  const toast = useToast();
  const currentCheck = useCurrentCheck();
  const seatAssignment = useSeatAssignment();
  const checkPrints = useCheckPrints({ checkId: currentCheck.query.data?.id });
  const removeCheckItem = useCheck.mutate.updateItemStatus();
  const scheduleItems = useCheck.mutate.sendAt();
  const repositionItems = useCheck.mutate.repositionItems();

  const voidReasonGetAll = useVoidReason.query.getAll();

  const voidReasonOptions = voidReasonGetAll.data?.items.map((vr) => ({ value: vr.id, label: vr.name }));

  const [isProcessing, isProcessingSet] = useState(false);
  const [activeCheckItem, setActiveCheckItem] = useState<CheckItemDef | undefined>(undefined);
  const [discountCheckItem, discountCheckItemSet] = useState(undefined as CheckItemDef | undefined);
  const [voidReasonCheckItem, voidReasonCheckItemSet] = useState(undefined as CheckItemDef | undefined);
  const [scheduleCheckItem, scheduleCheckItemSet] = useState(undefined as CheckItemDef | undefined);
  const [selectedCheckItemIds, selectedCheckItemIdsSet] = useState([] as CheckItemDef['id'][]);
  const isCheckItemIdSelected = (checkItemId: CheckItemDef['id']) => selectedCheckItemIds.includes(checkItemId);

  const isSomeSelected = Boolean(selectedCheckItemIds.length);

  const activeCheckItems = useMemo(() => {
    if (!currentCheck.query.data) return [];
    return currentCheck.query.data.items.filter((item) => CheckItem.IsActive(currentCheck.query.data as Check, item));
  }, [currentCheck.query.data]);

  const sortedActiveCheckItems: {
    asArray: typeof activeCheckItems,
    asRecord: Record<typeof activeCheckItems[number]['status'] | 'Scheduled', typeof activeCheckItems>,
  } = useMemo(() => {
    // this grouping is only useful when we are not using hotkeys
    if (seatAssignment.type === 'UseHotkeys') {
      return {
        asArray: [],
        asRecord: {} as Record<typeof activeCheckItems[number]['status'] | 'Scheduled', typeof activeCheckItems>,
      };
    }
    // group by item status
    const groupedActiveCheckItems = activeCheckItems.reduce((acc, item) => {
      acc[item.status] = acc[item.status] ?? [];
      acc[item.status].push(item);
      return acc;
    }, {} as Record<typeof activeCheckItems[number]['status'] | 'Scheduled', typeof activeCheckItems>);
    // separate Scheduled (i.e. Sent with schedule) items from Sent (i.e. Sent without schedule) items
    const sentItems = (groupedActiveCheckItems['Added'] ?? []).filter((i) => !i.sendSchedule);
    const scheduledItems = (groupedActiveCheckItems['Added'] ?? []).filter((i) => !!i.sendSchedule);
    groupedActiveCheckItems['Added'] = [...sentItems];
    groupedActiveCheckItems['Scheduled'] = [...scheduledItems];
    // piece array back together in the right order:
    // Sent -> Unsent -> Scheduled -> Held
    return {
      asArray: [
        ...(groupedActiveCheckItems['Sent'] ?? []).sort(sortByPosition),
        ...(groupedActiveCheckItems['Added'] ?? []).sort(sortByPosition),
        ...(groupedActiveCheckItems['Scheduled'] ?? []).sort(sortByPosition),
        ...(groupedActiveCheckItems['IndefiniteHold'] ?? []).sort(sortByPosition),
      ],
      asRecord: groupedActiveCheckItems,
    };
  }, [activeCheckItems, seatAssignment.type]);

  const sortedActiveCheckItemsWithSeats: {
    asArray: typeof activeCheckItems,
    asMapOfRecords: Map<typeof activeCheckItems[number]['seatNames'][number], Record<typeof activeCheckItems[number]['status'] | 'Scheduled', typeof activeCheckItems>>,
  } = useMemo(() => {
    // this grouping is only useful when we are using hotkeys
    if (seatAssignment.type !== 'UseHotkeys') {
      return {
        asArray: [],
        asMapOfRecords: {} as Map<typeof activeCheckItems[number]['seatNames'][number], Record<typeof activeCheckItems[number]['status'] | 'Scheduled', typeof activeCheckItems>>,
      };
    }
    // group items by seat
    const groupedActiveCheckItemsBySeat = activeCheckItems.reduce((acc, item) => {
      // when using hotkeys, items can only have a single seat name (thank goodness)
      if (item.seatNames.length === 0) {
        acc['table'] = acc['table'] ?? [];
        acc['table'].push(item);
      } else {
        acc[item.seatNames[0]] = acc[item.seatNames[0]] ?? [];
        acc[item.seatNames[0]].push(item);
      }
      return acc;
    }, {} as Record<typeof activeCheckItems[number]['seatNames'][number], typeof activeCheckItems>);
    const mappedActiveCheckItemsBySeat = new Map<typeof activeCheckItems[number]['seatNames'][number], Record<typeof activeCheckItems[number]['status'] | 'Scheduled', typeof activeCheckItems>>();
    // sort items in each seat
    for (const [seat, items] of Object.entries(groupedActiveCheckItemsBySeat)) {
      // group by item status
      const groupedActiveCheckItems = items.reduce((acc, item) => {
        acc[item.status] = acc[item.status] ?? [];
        acc[item.status].push(item);
        return acc;
      }, {} as Record<typeof activeCheckItems[number]['status'] | 'Scheduled', typeof activeCheckItems>);
      // separate Scheduled (i.e. Sent with schedule) items from Sent (i.e. Sent without schedule) items
      const sentItems = (groupedActiveCheckItems['Added'] ?? []).filter((i) => !i.sendSchedule);
      const scheduledItems = (groupedActiveCheckItems['Added'] ?? []).filter((i) => !!i.sendSchedule);
      groupedActiveCheckItems['Added'] = [...sentItems];
      groupedActiveCheckItems['Scheduled'] = [...scheduledItems];
      // piece array back together in the right order
      // Sent -> Unsent -> Scheduled -> Held
      groupedActiveCheckItemsBySeat[seat] = [
        ...(groupedActiveCheckItems['Sent'] ?? []).sort(sortByPosition),
        ...(groupedActiveCheckItems['Added'] ?? []).sort(sortByPosition),
        ...(groupedActiveCheckItems['Scheduled'] ?? []).sort(sortByPosition),
        ...(groupedActiveCheckItems['IndefiniteHold'] ?? []).sort(sortByPosition),
      ];
      mappedActiveCheckItemsBySeat.set(seat, groupedActiveCheckItems);
    }
    return {
      asArray: Object.keys(groupedActiveCheckItemsBySeat).flatMap((key) => groupedActiveCheckItemsBySeat[key]),
      asMapOfRecords: mappedActiveCheckItemsBySeat,
    };
  }, [activeCheckItems, seatAssignment.type]);

  const selectedCheckItems = useMemo(() => {
    if (!currentCheck.query.data) return [];
    return currentCheck.query.data.items.filter(({ id }) => selectedCheckItemIds.includes(id));
  }, [currentCheck.query.data, selectedCheckItemIds]);

  const findActiveCheckItem = (checkItemId: CheckItemDef['id']) => activeCheckItems.find(({ id }) => id === checkItemId);

  const unsentCheckItems = (currentCheck.query.data?.items || []).filter(({ status }) => status === 'Added');

  const scheduleItemForm = useForm<{ sendAt: string, isPM: boolean }>({
    defaultValues: { sendAt: '0', isPM: false },
    schema: object().shape({
      sendAt: string().label(t('checkItem.labels.sendAt')).matches(/^(0[1-9]|1[0-2]):([0-5][0-9])$/i, t('checkItem.errors.time')).required(),
    }),
  });

  const voidItemForm = useForm<{ reasonId: number }>({
    defaultValues: { reasonId: undefined },
    schema: object().shape({
      reasonId: number().label(t('voidReason.label')).required(),
    }),
  });

  const onEditOne = async (item: CheckItemDef) => {
    navigate(crumbs.pos.check.item.edit.interpolate({
      checkId: currentCheck.query.data?.id,
      checkItemId: item.id,
      menuItemId: item.menuItem.menuItemId,
      itemSelect: route.params.value.one,
    }));
  };

  const onCheckItemSingleSelect = async (checkItemId: CheckItemDef['id']) => {
    // block ui if we are processing
    if (isProcessing) return;
    // block ui in readonly mode
    if (mode === 'readonly') return;
    const item = findActiveCheckItem(checkItemId);
    // exit if we don't find an item (ts guard)
    if (!item) return log.debug(sectionId, 'onTap', 'item not found');

    const isSelected = isCheckItemIdSelected(checkItemId);
    if (isSelected) {
      setActiveCheckItem(undefined);
      selectedCheckItemIdsSet([]);
      navigate(crumbs.pos.check.main.interpolate({
        checkId: currentCheck.query.data?.id,
        itemSelect: route.params.value.one,
      }));
    } else {
      setActiveCheckItem(item);
      // set to edit a single check item
      selectedCheckItemIdsSet([checkItemId]);
      await onEditOne(item);
    }
  };

  const onCheckItemMultiSelect = async (checkItemId: CheckItemDef['id']) => {
    // block ui if we are processing
    if (isProcessing) return;
    // block ui in readonly mode
    if (mode === 'readonly') return;
    const item = findActiveCheckItem(checkItemId);
    // exit if we don't find an item (ts guard)
    if (!item) return log.debug(sectionId, 'onTap', 'item not found');
    setActiveCheckItem(item);
    const isSelected = isCheckItemIdSelected(checkItemId);
    let nextSelectedCheckItemIds = selectedCheckItemIds;
    if (isSelected) {
      nextSelectedCheckItemIds = selectedCheckItemIds.filter((id) => id !== checkItemId);
    } else {
      nextSelectedCheckItemIds.push(checkItemId);
    }
    selectedCheckItemIdsSet([...nextSelectedCheckItemIds]);
    // when we have unselected all check items
    if (!nextSelectedCheckItemIds.length) {
      setActiveCheckItem(undefined);
      // send back to the main route as single-select mode
      navigate(crumbs.pos.check.main.interpolate({
        checkId: currentCheck.query.data?.id,
        itemSelect: route.params.value.one,
      }));
      return;
    }

    if (mode === 'edit-many') {
      if (nextSelectedCheckItemIds.length === 1) {
        // grab the "last" selected check item
        const nextCheckItem = findActiveCheckItem(nextSelectedCheckItemIds[0]) as CheckItemDef;
        setActiveCheckItem(item);
        await onEditOne(nextCheckItem);
      }
    } else if (mode === 'edit-one') {
      // when in "edit-one" mode and we need to transition to multi-select mode
      if (nextSelectedCheckItemIds.length > 1) {
        // send back to the main route for check
        navigate(crumbs.pos.check.main.interpolate({
          checkId: currentCheck.query.data?.id,
          // change to multi-select mode
          itemSelect: route.params.value.many,
        }));
      } else {
        await onEditOne(item);
      }
    }
  };

  /**
   * When repositioning check items, the order of operations goes like this:
   *   1. items added to check
   *   2. API automatically assigns positions (CheckItem.position) based on order in which items are added
   *   3. move items up or down to reorder -> handle this logic on the client
   *   3. send reordered array of ids to API
   *   4. API assigns new positions based on the array that was sent from the client
   *   5. client displays items according to positions sent back from API
  */
  const onMoveItems = useCallback((selectedIds: CheckItemDef['id'][], direction: 'up' | 'down') => {
    let allIds: string[];
    let selectedItems: typeof activeCheckItems;
    if (seatAssignment.type === 'UseHotkeys') {
      // ids of all active check items - sorted
      allIds = sortedActiveCheckItemsWithSeats.asArray.map(({ id }) => id);
      // make sure the selected item ids are also sorted
      selectedItems = sortedActiveCheckItemsWithSeats.asArray.filter(({ id }) => selectedIds.includes(id));
    } else {
      // ids of all active check items - sorted
      allIds = sortedActiveCheckItems.asArray.map(({ id }) => id);
      // make sure the selected item ids are also sorted
      selectedItems = sortedActiveCheckItems.asArray.filter(({ id }) => selectedIds.includes(id));
    }
    const sortedSelectedItems = selectedItems.sort(sortByPosition);
    const sortedSelectedIds = sortedSelectedItems.map(({ id }) => id);
    // index of the first selected item
    const firstIdx = allIds.indexOf(sortedSelectedIds[0]);
    // index of the last seleted item
    const lastIdx = allIds.indexOf(sortedSelectedIds[sortedSelectedIds.length - 1]);
    // remove selectedIds from the original array (allIds)
    const nextIds = allIds.filter((id) => !selectedIds.includes(id));
    if (direction === 'up') {
      if (firstIdx === 0) {
        // move selectedIds to the beginning of the array
        nextIds.splice(0, 0, ...sortedSelectedIds);
      } else {
        // move selectedIds to the index preceding the index of the first selected item
        nextIds.splice(firstIdx - 1, 0, ...sortedSelectedIds);
      }
    } else if (direction === 'down') {
      if (lastIdx === allIds.length - 1) {
        // move selected ids to the end of the array
        nextIds.push(...sortedSelectedIds);
      } else {
        // element of the original array (allIds) after the last selected element
        const elAfterLast = allIds[lastIdx + 1];
        // move selectedIds to the index after the index of the last selected item
        const targetIdx = nextIds.indexOf(elAfterLast) + 1;
        nextIds.splice(targetIdx, 0, ...sortedSelectedIds);
      }
    }
    repositionItems.mutate({ checkId: currentCheck.currentId, itemIds: nextIds }, {
      onError: (e) => toast.show.serviceError(e, { id: `${sectionId}-onMoveItems-repositionItems` }),
    });
  }, [currentCheck.currentId, repositionItems, seatAssignment.type, sortedActiveCheckItems.asArray, sortedActiveCheckItemsWithSeats.asArray, toast.show]);

  const modals = {
    discount: {
      ...useModalControl(),
      item: discountCheckItem,
      onOpen: (itemId: CheckItem['id']) => {
        discountCheckItemSet(findActiveCheckItem(itemId));
        modals.discount.open();
      },
      onDone: () => {
        modals.discount.close();
        discountCheckItemSet(undefined);
      },
    },
    voidReason: {
      ...useModalControl(),
      item: voidReasonCheckItem,
      onOpen: (itemId: CheckItem['id']) => {
        voidReasonCheckItemSet(findActiveCheckItem(itemId));
        modals.voidReason.open();
        voidItemForm.action.reset({ reasonId: undefined });
      },
      onSave: async () => {
        const { isValid, values } = await voidItemForm.action.submit();

        if (!isValid) return log.info('modals.voidReason.onSave()', 'form !isValid');

        const reason = voidReasonOptions?.find((vro) => vro.value === values.reasonId)?.label ?? '';
        await removeCheckItem.mutateAsync({ checkId: currentCheck.query.data?.id as number, itemIds: [modals.voidReason.item?.id as string], reason, reasonId: values.reasonId, status: 'void' });

        voidReasonCheckItemSet(undefined);
        modals.voidReason.close();
      },
      onClose: () => {
        voidReasonCheckItemSet(undefined);
        modals.voidReason.close();
      },
    },
    schedule: {
      ...useModalControl(),
      item: scheduleCheckItem,
      onOpen: (itemId: CheckItem['id']) => {
        scheduleCheckItemSet(findActiveCheckItem(itemId));
        modals.schedule.open();
        scheduleItemForm.action.reset({ sendAt: '0', isPM: false });
      },
      onSave: async () => {

        const { isValid, values } = await scheduleItemForm.action.submit();

        if (!isValid) return log.info('modals.schedule.onSave()', 'form !isValid');

        const item = modals.schedule.item as CheckItem;
        const [sendAtHour, sendAtMinute] = values.sendAt ? values.sendAt.toString().split(':') : ['', ''];
        let sendAtHourInt = parseInt(sendAtHour, 10);
        if (values.isPM) {
          if (sendAtHourInt !== 12) {
            sendAtHourInt = sendAtHourInt + 12;
          }
        } else if (sendAtHourInt === 12) {
          // 12:00 AM -> 00:00
          sendAtHourInt = 0;
        }
        scheduleItems.mutate({
          checkId: currentCheck.query.data?.id as number,
          itemIds: [item.id],
          sendAt: `${sendAtHourInt.toString().padStart(2, '0')}:${sendAtMinute.toString().padStart(2, '0')}:00`,
        }, {
          onError: (error) => {
            toast.show.serviceError(error, { id: `${sectionId}.scheduleItems` });
            scheduleCheckItemSet(undefined);
            modals.schedule.close();
          },
          onSuccess: () => {
            scheduleCheckItemSet(undefined);
            modals.schedule.close();
          },
        });
      },
      onClose: () => {
        scheduleCheckItemSet(undefined);
        modals.schedule.close();
      },
    },
  };

  const onSaveCheckItemVoidReason = useCallback(() => {
    void modals.voidReason.onSave();
  }, [modals.voidReason]);

  const onSaveCheckItemSchedule = useCallback(() => {
    void modals.schedule.onSave();
  }, [modals.schedule]);


  // reset state when new check is loading
  useEffect(() => {
    if (currentCheck.query.isLoading) {
      setActiveCheckItem(undefined);
      discountCheckItemSet(undefined);
      voidReasonCheckItemSet(undefined);
      scheduleCheckItemSet(undefined);
      selectedCheckItemIdsSet([]);
    }
  }, [currentCheck.query.isLoading]);

  return {
    mode,
    activeCheck: currentCheck.query.data,
    isProcessing,
    isProcessingSet,
    isSomeSelected,
    isCheckItemIdSelected,
    activeCheckItem,
    activeCheckItems,
    sortedActiveCheckItems,
    sortedActiveCheckItemsWithSeats,
    selectedCheckItemIds,
    selectedCheckItemIdsSet,
    selectedCheckItems,
    unsentCheckItems,
    modals,
    checkPrints,
    onSaveCheckItemVoidReason,
    onSaveCheckItemSchedule,
    voidItemForm,
    voidReasonOptions,
    scheduleItemForm,
    onCheckItemSingleSelect,
    onCheckItemMultiSelect,
    onMoveItems,
    processing: {
      repositionItems: repositionItems.isPending,
      getCheck: currentCheck.query.isFetching,
    },
  };
};

type UsedCurrentCheckItemButtons = ReturnType<typeof useCurrentCheckItemButtons>;

export { useCurrentCheckItemButtons };
export type { UseCheckItemButtonsOptions, UsedCurrentCheckItemButtons };
